import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Target, ArrowRight, Loader2 } from "lucide-react";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";
import AffiliateBanner from "@/components/AffiliateBanner";

const HERO_BG =
  "https://images.unsplash.com/photo-1487958449943-2429e8be8625?auto=format&fit=crop&w=1600&q=80";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    setBusy(true);
    const r = await register(email.trim(), password, name.trim() || null);
    setBusy(false);
    if (r.ok) {
      toast.success("Account created. Welcome.");
      nav("/dashboard", { replace: true });
    } else {
      toast.error(r.error || "Registration failed");
    }
  };

  return (
    <div className="min-h-screen bg-ink-50">
      <AffiliateBanner />
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
      <div className="flex flex-col justify-center px-6 sm:px-12 py-16 bg-white">
        <div className="max-w-md w-full mx-auto">
          <div className="flex items-center gap-3 mb-12">
            <img
              src="/logo-128.png"
              alt="LeadIQ Pro"
              width="48"
              height="48"
              className="w-12 h-12 shrink-0"
            />
            <div className="leading-tight">
              <div className="font-display font-extrabold text-lg tracking-tight text-ink-900">
                LeadIQ Pro
              </div>
              <div className="text-[10px] uppercase tracking-widestx text-ink-400 font-mono">
                Lead Enrichment Engine
              </div>
            </div>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-none font-extrabold text-ink-900 mb-3">
            Create account.
          </h1>
          <p className="text-ink-500 text-sm mb-10 max-w-sm">
            Set up your workspace in seconds. No credit card required for the beta.
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="register-form">
            <GoogleSignInButton testid="register-google-button" label="Sign up with Google" />
            <div className="flex items-center gap-3 text-[10px] uppercase tracking-widestx text-ink-400 font-mono">
              <span className="h-px bg-ink-100 flex-1" />
              or use email
              <span className="h-px bg-ink-100 flex-1" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widestx font-semibold text-ink-500">
                Full name <span className="text-ink-300 normal-case tracking-normal">(optional)</span>
              </Label>
              <Input
                data-testid="register-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Doe"
                className="mt-2 rounded-sm border-ink-200 focus:border-brand focus-visible:ring-brand h-11"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widestx font-semibold text-ink-500">
                Work email
              </Label>
              <Input
                data-testid="register-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="mt-2 rounded-sm border-ink-200 focus:border-brand focus-visible:ring-brand h-11 font-mono text-sm"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widestx font-semibold text-ink-500">
                Password
              </Label>
              <Input
                data-testid="register-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                className="mt-2 rounded-sm border-ink-200 focus:border-brand focus-visible:ring-brand h-11"
              />
            </div>
            <Button
              data-testid="register-submit-button"
              type="submit"
              disabled={busy}
              className="w-full h-11 rounded-sm bg-brand hover:bg-brand-hover text-white font-medium tracking-wide group"
            >
              {busy ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Create account
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-8 text-sm text-ink-500">
            Already have an account?{" "}
            <Link to="/login" data-testid="go-to-login-link" className="text-brand hover:text-brand-hover font-medium underline-offset-4 hover:underline">
              Sign in
            </Link>
          </div>
        </div>
      </div>

      <div className="hidden lg:block relative overflow-hidden">
        <img src={HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-900/80 via-ink-900/30 to-ink-900/10" />
        <div className="relative h-full flex flex-col justify-end p-12">
          <div className="max-w-md">
            <div className="text-[10px] uppercase tracking-widestx font-mono text-white/80 mb-4">
              for sales teams
            </div>
            <h2 className="font-display text-4xl font-light text-white leading-tight tracking-tight">
              Stop chasing.<br />
              <span className="font-extrabold">Start qualifying.</span>
            </h2>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
